import { Badge } from "@bcc/components/ui/badge";
import { Button } from "@bcc/components/ui/button";
import { Checkbox } from "@bcc/components/ui/checkbox";
import { Label } from "@bcc/components/ui/label";
import { sanitizeAeroPressGrind } from "@client-shared/utilities/productSyncUtilities";
import type { FormVariant } from "@shared/types";
import { Pencil } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import type { GrindOption, ProductSyncOption } from "./product-sync-modal.types";
import {
  setOverrideButtonTestId,
  setSingleVariantRowProductNameTestId,
  setSingleVariantRowVariantNameTestId,
  setVariantRowGrindCheckboxTestId,
  setVariantRowGrindsLayoutTestId,
  setVariantRowTestId,
} from "./product-sync-modal.utils";

export interface VariantRowSingleProps {
  value: ProductSyncOption;
  handleChange: (targetVariantId: string) => (grinds: string[]) => void;
  grindsOptions: GrindOption[];
  remoteVariant?: FormVariant;
  remoteProductTitle?: string;
  isGrindMappingDisabled: boolean;
}

export function VariantRowSingle({
  value,
  handleChange,
  grindsOptions,
  remoteVariant,
  isGrindMappingDisabled,
  remoteProductTitle,
}: Readonly<VariantRowSingleProps>) {
  const { t } = useTranslation();
  const [selectedRowSingleGrinds, setSelectedRowSingleGrinds] = useState<string[]>([]);
  const [hasGrindOverride, setHasGrindOverride] = useState(false);
  const hasSetGrindTypeRef = useRef(false);

  const targetVariantId = remoteVariant?.id ?? value.value;

  const detectedGrindType = useMemo(() => {
    if (!remoteVariant?.grind || !remoteVariant.hasDetectedGrindType || !remoteVariant.hasValidDetectedGrindType) {
      return undefined;
    }
    if (remoteVariant.detectedGrindInfo?.some((detectedGrind) => detectedGrind.mapDetectedGrindTo)) {
      return remoteVariant.detectedGrindInfo.map((detectedGrind) =>
        sanitizeAeroPressGrind(detectedGrind.mapDetectedGrindTo ?? detectedGrind.grind)
      );
    }
    return remoteVariant.grind;
  }, [remoteVariant]);

  useEffect(() => {
    if (!detectedGrindType) {
      return;
    }
    // eslint-disable-next-line react-hooks/set-state-in-effect -- sync selected grinds when the detected grind type changes for this row
    setSelectedRowSingleGrinds(detectedGrindType);
  }, [detectedGrindType]);

  useEffect(() => {
    // Existing mapping rows already carry their saved grindTypes in the
    // parent ProductSync state — auto-applying the remote variant's
    // detected grind here would dirty the form and let Save overwrite the
    // saved mapping (e.g. a Drip-mapped row sharing a WB-detected remote
    // variant would silently flip back to Whole bean).
    if (isGrindMappingDisabled) {
      return;
    }
    if (remoteVariant?.isImportedLocalVariant) {
      return;
    }
    if (hasSetGrindTypeRef.current || !detectedGrindType || detectedGrindType.length === 0) {
      return;
    }
    hasSetGrindTypeRef.current = true;
    handleChange(targetVariantId)(detectedGrindType);
  }, [detectedGrindType, handleChange, isGrindMappingDisabled, remoteVariant?.isImportedLocalVariant, targetVariantId]);

  const singleRowGrindOptions = useMemo(() => {
    if (isGrindMappingDisabled) {
      // Existing mappings: trust the `checked` already computed by
      // VariantRowPlaceholder.getGrindOptions from the saved connection — that
      // is the ground truth. Fall back to local selection state only when the
      // upstream did not set it (e.g. unit tests that pass options directly).
      return grindsOptions.map((option) => ({
        ...option,
        checked: option.checked ?? selectedRowSingleGrinds.includes(option.value),
      }));
    }

    if (!detectedGrindType || detectedGrindType.length === 0 || hasGrindOverride) {
      return grindsOptions.map((option) => {
        const isOptionChecked = selectedRowSingleGrinds.includes(option.value);
        return {
          ...option,
          checked: isOptionChecked,
          disabled: isOptionChecked ? false : option.disabled,
        } satisfies GrindOption;
      });
    }
    return grindsOptions.map((option) => {
      return {
        ...option,
        checked: detectedGrindType.includes(option.value),
        disabled: true,
      } satisfies GrindOption;
    });
  }, [detectedGrindType, grindsOptions, hasGrindOverride, isGrindMappingDisabled, selectedRowSingleGrinds]);

  const handleToggleOverride = () => {
    if (hasGrindOverride && selectedRowSingleGrinds && detectedGrindType) {
      handleChange(targetVariantId)(detectedGrindType);
      setSelectedRowSingleGrinds(detectedGrindType);
    }
    setHasGrindOverride(!hasGrindOverride);
  };

  const handleSingleChange = (grindValue: string, checked: boolean) => {
    const currentSelection = new Set(selectedRowSingleGrinds);
    if (checked) {
      currentSelection.add(grindValue);
    } else {
      currentSelection.delete(grindValue);
    }
    const nextSelection = Array.from(currentSelection);
    setSelectedRowSingleGrinds(nextSelection);
    handleChange(targetVariantId)(nextSelection);
  };

  return (
    <div key={value.value} className="rounded-md border bg-muted/20 px-3 py-3" data-testid={setVariantRowTestId(value.value)}>
      <div className="flex flex-col gap-3 xl:flex-row xl:items-start xl:justify-between">
        <div className="min-w-0 xl:min-w-[240px] xl:max-w-[280px]">
          {remoteProductTitle && remoteVariant?.title ? (
            <div className="flex flex-wrap items-center gap-2">
              <Badge variant="secondary" testId={setSingleVariantRowProductNameTestId(value.value)}>
                {remoteProductTitle}
              </Badge>
              <Badge variant="outline" testId={setSingleVariantRowVariantNameTestId(value.value)}>
                {remoteVariant.title}
              </Badge>
            </div>
          ) : (
            <p className="text-sm text-foreground">{value.label}</p>
          )}
        </div>
        <div
          data-testid={setVariantRowGrindsLayoutTestId(value.value)}
          className="grid flex-1 gap-2 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4"
        >
          {singleRowGrindOptions.map((option) => {
            return (
              <Label
                key={`${value.value}-${option.value}`}
                className="flex items-center gap-2 rounded-md border px-2 py-1.5 text-sm font-normal"
              >
                <Checkbox
                  checked={Boolean(option.checked)}
                  disabled={Boolean(option.disabled)}
                  onCheckedChange={(checked) => {
                    handleSingleChange(option.value, checked === true);
                  }}
                  testId={setVariantRowGrindCheckboxTestId(value.value, option.value)}
                />
                <span>{option.label}</span>
              </Label>
            );
          })}
        </div>
        {!isGrindMappingDisabled && detectedGrindType && (
          <Button
            type="button"
            variant="secondary"
            size="sm"
            onClick={handleToggleOverride}
            className="self-start bg-gray-100 text-gray-700 hover:bg-gray-200 xl:self-center"
            testId={setOverrideButtonTestId(value.value)}
          >
            <Pencil className="h-3.5 w-3.5" />
            {hasGrindOverride
              ? t("components.ProductSyncModal.handleOverride.revert", {
                  defaultValue: "Revert",
                })
              : t("components.ProductSyncModal.handleOverride.set", {
                  defaultValue: "Override",
                })}
          </Button>
        )}
      </div>
    </div>
  );
}
